import React, { useState, useEffect } from 'react';
import { X, Search, DollarSign, FileText } from 'lucide-react';

const IfadaOrderSearchPaymentModal = ({ onClose, onPaymentSuccess, apiCall, selectedYear, showError, showSuccess }) => {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [paymentMethod, setPaymentMethod] = useState('Cash');
  const [processingPayment, setProcessingPayment] = useState(false);

  useEffect(() => {
    loadOrders();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadOrders = async () => {
    try {
      setLoading(true);
      const data = await apiCall('/ifada/orders', {
        method: 'GET'
      });
      // Filter only pending/unpaid orders
      const pendingOrders = (data || []).filter(
        o => o.PaymentStatus === 'Pending' || o.PaymentStatus === 'Unpaid'
      );
      setOrders(pendingOrders);
    } catch (error) {
      console.error('Error loading orders:', error);
      showError('Error loading orders: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  const formatLBP = (amount) => {
    return new Intl.NumberFormat('en-US').format(amount) + ' ل.ل';
  };

  const getFilteredOrders = () => {
    if (!searchTerm) return orders;
    
    const search = searchTerm.toLowerCase();
    return orders.filter(order => 
      order.OrderID.toString().includes(search) ||
      (order.StudentName && order.StudentName.toLowerCase().includes(search)) ||
      (order.FamilyID && order.FamilyID.toLowerCase().includes(search)) ||
      (order.StudentClass && order.StudentClass.toLowerCase().includes(search))
    );
  };

  const handlePayment = async () => {
    if (!selectedOrder) return;
    
    try {
      setProcessingPayment(true);
      
      const paymentData = {
        familyId: selectedOrder.FamilyID,
        paymentMethod: paymentMethod,
        academicYear: selectedYear
      };
      
      const result = await apiCall(`/ifada/orders/${selectedOrder.OrderID}/pay`, {
        method: 'POST',
        body: JSON.stringify(paymentData)
      });
      
      showSuccess(`Payment recorded successfully! Receipt #${result.paymentId}`);
      
      // Call success callback to trigger receipt
      if (onPaymentSuccess) {
        onPaymentSuccess(result.paymentId);
      }
      
      // Remove paid order from list
      setOrders(orders.filter(o => o.OrderID !== selectedOrder.OrderID));
      setSelectedOrder(null);
      
      // Close modal after short delay
      setTimeout(() => onClose(), 500);
    } catch (error) {
      showError('Error recording payment: ' + error.message);
    } finally {
      setProcessingPayment(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-2xl max-w-6xl w-full max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="p-6 border-b flex justify-between items-center bg-purple-50">
          <div>
            <h3 className="text-2xl font-bold text-gray-800">Ifada Payment - Record Payment</h3>
            <p className="text-sm text-gray-600 mt-1">
              إفادات ومستندات / Search and pay for pending Ifada orders
            </p>
          </div>
          <button onClick={onClose} className="text-gray-500 hover:text-gray-700">
            <X size={24} />
          </button>
        </div>

        <div className="flex flex-1 overflow-hidden">
          {/* Left Side - Order List */}
          <div className="w-1/2 border-r flex flex-col">
            {/* Search */}
            <div className="p-4 border-b bg-gray-50">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={20} />
                <input
                  type="text"
                  placeholder="Search by Order #, Student, Family ID, Class..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full pl-10 pr-4 py-2 border rounded-lg focus:ring-2 focus:ring-purple-500"
                />
              </div>
              <p className="text-xs text-gray-500 mt-2">
                {getFilteredOrders().length} pending order(s) found
              </p>
            </div>

            {/* Orders List */}
            <div className="flex-1 overflow-y-auto p-4 space-y-2">
              {loading ? (
                <div className="text-center py-8 text-gray-500">Loading orders...</div>
              ) : getFilteredOrders().length === 0 ? (
                <div className="text-center py-8 text-gray-500">
                  <FileText size={48} className="mx-auto mb-2 text-gray-300" />
                  <p>No pending Ifada orders found</p>
                  {searchTerm && (
                    <p className="text-sm mt-1">Try a different search term</p>
                  )}
                </div>
              ) : (
                getFilteredOrders().map(order => (
                  <div
                    key={order.OrderID}
                    onClick={() => setSelectedOrder(order)}
                    className={`p-4 border rounded-lg cursor-pointer transition-all ${
                      selectedOrder?.OrderID === order.OrderID
                        ? 'border-purple-500 bg-purple-50 shadow-md'
                        : 'border-gray-200 hover:border-purple-300 hover:bg-gray-50'
                    }`}
                  >
                    <div className="flex justify-between items-start mb-2">
                      <div>
                        <span className="font-bold text-lg text-purple-600">#{order.OrderID}</span>
                        <span className="ml-2 text-xs bg-yellow-100 text-yellow-800 px-2 py-0.5 rounded">
                          {order.PaymentStatus}
                        </span>
                      </div>
                      <div className="text-right">
                        <div className="font-bold text-purple-600">{formatLBP(order.TotalAmount)}</div>
                        <div className="text-xs text-gray-500">
                          {new Date(order.OrderDate).toLocaleDateString()}
                        </div>
                      </div>
                    </div>
                    
                    <div className="space-y-1">
                      <div className="flex items-center gap-2">
                        <span className="text-sm font-semibold text-gray-700">
                          {order.StudentName}
                        </span>
                        {order.IsManualEntry && (
                          <span className="text-xs bg-gray-100 text-gray-600 px-1.5 py-0.5 rounded">
                            Manual
                          </span>
                        )}
                      </div>
                      
                      {order.StudentClass && (
                        <div className="text-sm text-gray-600">
                          Class: {order.StudentClass}
                        </div>
                      )}
                      
                      {order.FamilyID && (
                        <div className="text-xs text-gray-500">
                          Family: {order.FamilyID}
                        </div>
                      )}
                      
                      <div className="text-xs text-gray-500">
                        Items: {order.ItemCount || 1}
                      </div>
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>

          {/* Right Side - Payment Details */}
          <div className="w-1/2 flex flex-col">
            {selectedOrder ? (
              <>
                {/* Order Details */}
                <div className="flex-1 overflow-y-auto p-6">
                  <h4 className="text-lg font-bold text-gray-800 mb-4">Order Details</h4>
                  
                  <div className="bg-purple-50 rounded-lg p-4 mb-6 border border-purple-200">
                    <div className="grid grid-cols-2 gap-4 mb-4">
                      <div>
                        <p className="text-sm text-gray-600">Order Number</p>
                        <p className="font-bold text-lg">#{selectedOrder.OrderID}</p>
                      </div>
                      <div>
                        <p className="text-sm text-gray-600">Date</p>
                        <p className="font-semibold">
                          {new Date(selectedOrder.OrderDate).toLocaleDateString()}
                        </p>
                      </div>
                    </div>

                    <div className="mb-4">
                      <p className="text-sm text-gray-600">Student</p>
                      <p className="font-semibold text-lg">{selectedOrder.StudentName}</p>
                      {selectedOrder.StudentClass && (
                        <p className="text-sm text-gray-500">Class: {selectedOrder.StudentClass}</p>
                      )}
                      {selectedOrder.IsManualEntry && (
                        <span className="text-xs bg-yellow-100 text-yellow-800 px-2 py-1 rounded mt-1 inline-block">
                          Manual Entry
                        </span>
                      )}
                    </div>

                    {selectedOrder.FamilyID && (
                      <div className="mb-4">
                        <p className="text-sm text-gray-600">Family ID</p>
                        <p className="font-semibold">{selectedOrder.FamilyID}</p>
                      </div>
                    )}

                    <div className="mb-4">
                      <p className="text-sm text-gray-600">Items</p>
                      <p className="font-semibold">{selectedOrder.ItemCount || 1} item(s)</p>
                    </div>

                    <div className="pt-4 border-t border-purple-300">
                      <p className="text-sm text-gray-600 mb-1">Total Amount</p>
                      <p className="text-3xl font-bold text-purple-600">
                        {formatLBP(selectedOrder.TotalAmount)}
                      </p>
                    </div>
                  </div>

                  {/* Payment Method Selection */}
                  <div className="mb-6">
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      Payment Method *
                    </label>
                    <select
                      value={paymentMethod}
                      onChange={(e) => setPaymentMethod(e.target.value)}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent text-lg"
                      disabled={processingPayment}
                    >
                      <option value="Cash">💵 Cash / نقداً</option>
                      <option value="Check">📝 Check / شيك</option>
                      <option value="Bank Transfer">🏦 Bank Transfer / تحويل بنكي</option>
                      <option value="Credit Card">💳 Credit Card / بطاقة ائتمان</option>
                    </select>
                  </div>

                  {/* Info Box */}
                  <div className="bg-blue-50 rounded-lg p-4 border border-blue-200">
                    <p className="text-sm text-blue-800">
                      💡 <strong>Note:</strong> This payment will be recorded in Lebanese Pounds (LBP) 
                      and will appear in the Ifada Payments tab with currency ل.ل
                    </p>
                  </div>
                </div>

                {/* Action Buttons */}
                <div className="p-6 border-t bg-gray-50">
                  <div className="flex gap-3">
                    <button
                      onClick={() => setSelectedOrder(null)}
                      className="flex-1 px-6 py-3 border-2 border-gray-300 rounded-lg hover:bg-gray-100 transition-colors font-semibold"
                      disabled={processingPayment}
                    >
                      Cancel Selection
                    </button>
                    <button
                      onClick={handlePayment}
                      disabled={processingPayment}
                      className="flex-1 px-6 py-3 bg-green-600 hover:bg-green-700 text-white rounded-lg flex items-center justify-center gap-2 transition-colors disabled:opacity-50 disabled:cursor-not-allowed font-semibold text-lg shadow-lg"
                    >
                      <DollarSign size={20} />
                      {processingPayment ? 'Processing...' : 'Record Payment'}
                    </button>
                  </div>
                </div>
              </>
            ) : (
              <div className="flex-1 flex items-center justify-center text-gray-400">
                <div className="text-center">
                  <FileText size={64} className="mx-auto mb-4 text-gray-300" />
                  <p className="text-lg">Select an order from the left</p>
                  <p className="text-sm mt-1">to record payment</p>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default IfadaOrderSearchPaymentModal;